/**
 * Which glassware a container is. Geometry lives in vesselGeometry.ts (one
 * radiusAt profile per shape); which stands each can be held in lives in
 * standTypes.ts.
 */
export type VesselType = 'flask' | 'beaker' | 'round-flask' | 'test-tube';

export const VESSEL_TYPES: VesselType[] = ['flask', 'beaker', 'round-flask', 'test-tube'];

export const VESSEL_LABELS: Record<VesselType, string> = {
  flask: 'Flask',
  beaker: 'Beaker',
  'round-flask': 'Round-Bottom Flask',
  'test-tube': 'Test Tube',
};

/**
 * What a bench actually fills each vessel to, in millilitres — a third of a
 * test tube, a titration's 100 mL in a 250 mL flask. Nobody fills to the brim:
 * the empty part is what swirling, boiling and effervescence need.
 *
 * This is the ANCHOR for the picture, and the only volume declared anywhere.
 * The liquid level was a hardcoded fraction per vessel (vesselGeometry.ts's
 * `fillT`) meaning nothing more than "a sensibly filled vessel", because there
 * were no quantities for it to mean. Declaring `fillT` to BE the working
 * volume keeps the bench looking exactly as it did, with every other amount
 * drawn relative to it.
 *
 * How much the vessel holds to the BRIM is not declared here, because it is
 * not free to declare: it follows from the shape the bench draws, and the
 * drawn glassware is deliberately not to scale (see FILL_SHARE). Ask
 * vesselGeometry's brimVolumeML, which works it out from the profile.
 */
export const WORKING_VOLUME_ML: Record<VesselType, number> = {
  flask: 80,
  beaker: 80,
  'round-flask': 80,
  'test-tube': 8,
};

/**
 * How many ordinary pours fill a vessel to that level.
 *
 * The level climbing is the thing the pour animation exists to show, and it
 * cannot show anything if one pour arrives at the mark: a test tube took 5 mL
 * of dilute acid and was simply full, so every pour after the first moved
 * nothing. Eight is the number that makes a pour visibly worth something and
 * still gets a vessel usable in a few of them.
 *
 * It is what fixes the volumes above rather than the other way round: the
 * working level is where the bench DRAWS the liquid, so it is the numbers of
 * millilitres that had to move — 8 mL in a tube and 80 in a beaker, so that a
 * standard pour is a round 1 mL or 10 mL.
 */
export const POURS_TO_WORKING = 8;

/** What a pour is multiplied by in this glassware. A dose is written at
 *  test-tube scale, since that is the scale of the school procedures being
 *  modelled. Ten, not five, because the large vessels hold ten times what a
 *  tube does at their working level — so the same POURS_TO_WORKING holds in
 *  every piece of glassware, and a pour is a round 10 mL rather than a stain
 *  on the floor of a beaker. */
export function pourScaleFor(vesselType: VesselType): number {
  return vesselType === 'test-tube' ? 1 : 10;
}

export function nextVesselType(index: number): VesselType {
  return VESSEL_TYPES[index % VESSEL_TYPES.length];
}
